import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Box,
  FormControl,
  MenuItem,
  FormHelperText,
  FormLabel,
  Select,
  Slider,
  RadioGroup,
  Radio,
  Typography,
} from "@mui/material";

import { TalentAssessmentEnum } from "../../../enum/reviewEnums";
import { AddProfessionalReviewCandidate } from "../../../types/addReviewCandidate";
import { ThroughVideosCardIcon, ThroughTryOutCardIcon } from "../../../assets/icons/components-icons";

const OverviewStep = () => {
  const { t } = useTranslation("goHire", { keyPrefix: "components.reviewCandidateModal" });
  const { t: tCommon } = useTranslation("common", { keyPrefix: "user.role" });
  const { t: tValidations } = useTranslation("goHire", { keyPrefix: "components.reviewCandidateModal.validations" });

  const { control } = useFormContext<AddProfessionalReviewCandidate>();

  const howDidYouAssessTheTalentOptions = [
    { value: TalentAssessmentEnum.VIDEOS, label: t("throughVideosAndPosts"), icon: <ThroughVideosCardIcon /> },
    { value: TalentAssessmentEnum.TRYOUT, label: t("throughTryoutInterview"), icon: <ThroughTryOutCardIcon /> },
  ];

  return (
    <Box sx={[styles.container, styles.containerPadding]}>
      <Controller
        name="whoIsReviewing"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <FormControl fullWidth component="fieldset" error={!!error} disabled>
            <FormLabel error={!!error}>{t("whoIsReviewing")}</FormLabel>
            <Select {...field} error={!!error} fullWidth>
              <MenuItem disabled selected value="ENTITY_ADMIN">
                {tCommon("ENTITY_ADMIN")}
              </MenuItem>
            </Select>
            {error?.message && <FormHelperText error>{t(error.message)}</FormHelperText>}
          </FormControl>
        )}
      />

      <Controller
        name="overallFitScore"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <FormControl fullWidth component="fieldset" error={!!error}>
            <FormLabel error={!!error}>{t("overallFitScore")}</FormLabel>
            <Slider
              value={field.value}
              onChange={(_, newValue) => field.onChange(newValue)}
              getAriaLabel={() => "Overall Fit Score"}
              valueLabelDisplay="auto"
            />
            {error && <FormHelperText error>{t(error.message as string)}</FormHelperText>}
          </FormControl>
        )}
      />
      <Controller
        name="talentAssessment"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <FormControl error={!!error} fullWidth component="fieldset" sx={styles.howDidYouAssessTheTalentFormControl}>
            <FormLabel error={!!error}>{t("howDidYouAssessTheTalent")}</FormLabel>
            <RadioGroup sx={styles.howDidYouAssessTheTalentRadioGroup}>
              {howDidYouAssessTheTalentOptions.map(({ value, label, icon }) => {
                return (
                  <Box
                    sx={styles.howDidYouAssessTheTalentCard(value === field.value, !!error)}
                    key={value}
                    onClick={() => field.onChange(value)}
                  >
                    <Radio
                      size="small"
                      value={value}
                      sx={{ position: "absolute", top: 0, right: 0 }}
                      checked={field.value === value}
                    />
                    {icon}
                    <Typography>{label}</Typography>
                  </Box>
                );
              })}
            </RadioGroup>
            {error && <FormHelperText error>{tValidations(error.message as string)}</FormHelperText>}
          </FormControl>
        )}
      />
    </Box>
  );
};

export default OverviewStep;

const styles = {
  containerPadding: {
    px: "24px",
  },
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  howDidYouAssessTheTalentRadioGroup: {
    display: "flex",
    flexDirection: "row",
    gap: "16px",
  },
  howDidYouAssessTheTalentCard: (isSelected: boolean, hasError: boolean) => ({
    position: "relative",
    flex: 1,
    border: "1.5px solid",
    borderColor: hasError
      ? "var(--Red---100, #FF0000)"
      : isSelected
      ? "var(--Green---100, #008B07)"
      : "var(--Gray---100, #E7E7E7)",
    background: isSelected ? "var(--Green---25, #EDF8EE)" : "#FFF",
    borderRadius: "12px",
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  }),
  howDidYouAssessTheTalentFormControl: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
};
